import { getAgentIcon } from '../AgentIcon'
import { ChatGlyph } from '../ChatInterface/ChatGlyph'
import SidebarSignature from './SidebarSignature'
import SidebarToggleButton from './SidebarToggleButton'
import { useChatAppearance } from '../../core/client/useChatAppearance'
import { ChatWordmark } from '../ChatInterface/ChatIdentity'
import {
  Avatar,
  Box,
  BoxProps,
  Divider,
  Flex,
  Input,
  IconButton,
  HStack,
  Heading,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { BarChart3 } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { Feature } from '../../core/client/features.gen'
import { useFeature } from '../../core/features/FeaturesProvider'
import { useAgents } from '../../hooks/agents/useAgents'
import { useUser } from '../../hooks/auth/useUser'
import GeneralInformationModal from '../GeneralInformationModal'

import { useInView } from 'react-intersection-observer'
import { useChats } from '../../hooks/chat/useChats'
import { useDeleteConversation } from '../../hooks/chat/useDeleteConversation'
import { useRenameConversation } from '../../hooks/chat/useRenameConversation'
import { useLanguage } from '../../hooks/utility/useLanguage'
import useMobile from '../../hooks/utility/useMobile'
import { MAIN_CHAT_AGENT_SLUG } from '../../types/agentTypes'
import { ChatSummarySchema } from '../../types/chatTypes'
import { sanitizeNameForInitials } from '../../utils/util'
import ConversationRow from './ConversationRow'
import SidebarItem from './SidebarItem'

interface ChatSideBarProps extends BoxProps {
  isLoadingDetails?: boolean
  onNewChat: () => void
  selectedConversationId?: string
  onSelectConversation: (id: string, agentId?: string) => void
  isSidebarOpenExternal?: boolean
  onSidebarToggle?: () => void
  isMinimized?: boolean
  onClearConversation?: () => void
  onSelectAgent?: (agentId: string) => void
}

const ChatSidebar: React.FC<ChatSideBarProps> = ({
  onNewChat,
  isLoadingDetails,
  selectedConversationId: selectedChatId,
  onSelectConversation: onSelectChat,
  isSidebarOpenExternal,
  onSidebarToggle,
  isMinimized,
  onClearConversation,
  onSelectAgent,
  ...props
}) => {
  const { sidebarWidth, showWordmark, branding } = useChatAppearance()
  const navigate = useNavigate()
  const isLibraryPage = useLocation().pathname.startsWith('/library')
  const isMobile = useMobile()
  const drawerWidth = isMobile ? '85vw' : sidebarWidth
  const { isOpen: isOpenInternal, onClose: onCloseInternal } = useDisclosure({ defaultIsOpen: !isMobile })
  const {
    isOpen: isInformationModalOpen,
    onOpen: openInformationModal,
    onClose: closeInformationModal,
  } = useDisclosure()
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const searchButtonRef = useRef<HTMLButtonElement>(null)
  // const { user } = useAppContext();
  const { t } = useLanguage()

  const { agentId } = useParams()

  // Use the unified chats hook with search functionality
  const {
    chatList: chats,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useChats({ searchQuery })

  const { renameConversation } = useRenameConversation()
  const { deleteConversation } = useDeleteConversation()

  const { agents, agentError, isLoadingAgents } = useAgents()
  const defaultAgent =
    agents.find(agent => agent.slug === 'gpt' && agent.type === 'privileged') ??
    // By slug before name: without an assistantName the main chat is named "<company> Chat".
    agents.find(agent => agent.slug === MAIN_CHAT_AGENT_SLUG) ??
    agents.find(agent => agent.name === branding?.assistantName && agent.type === 'privileged') ??
    agents.find(agent => agent.type === 'privileged')
  const { user } = useAuth()
  const { data: currentUser } = useUser()
  const dividerColor = useColorModeValue('gray.200', 'gray.600')

  // Determine whether to use internal or external state management
  const isExternallyControlled = isSidebarOpenExternal !== undefined && onSidebarToggle !== undefined
  const isOpen = isExternallyControlled ? isSidebarOpenExternal : isOpenInternal

  const closeSidebarOnMobile = useCallback(() => {
    if (!isMobile || !isOpen) return
    if (isExternallyControlled) {
      onSidebarToggle?.()
    } else {
      onCloseInternal()
    }
  }, [isMobile, isOpen, isExternallyControlled, onSidebarToggle, onCloseInternal])

  // Close sidebar when a chat is selected on mobile
  useEffect(() => {
    if (isMobile && selectedChatId && !isExternallyControlled) {
      onCloseInternal()
    }
  }, [selectedChatId, isMobile, onCloseInternal, isExternallyControlled])

  // Land on the default assistant when agents are loaded and none is selected yet.
  useEffect(() => {
    if (!isLibraryPage && !isLoadingAgents && agents.length > 0 && !agentId && onSelectAgent && defaultAgent) {
      onSelectAgent(defaultAgent.id)
    }
  }, [isLibraryPage, isLoadingAgents, agents, agentId, onSelectAgent, defaultAgent])

  const handleChatItemClick = useCallback(
    (chatId: string) => {
      const chat = chats.find(c => c.id === chatId)

      if (chat && chat?.agentId) {
        onSelectChat(chatId, chat.agentId)
      } else {
        // Legacy chat without agent
        onSelectChat(chatId)
      }
      closeSidebarOnMobile()
    },
    [onSelectChat, chats, closeSidebarOnMobile],
  )

  const handleDeleteChat = useCallback(
    (chatId: string) => {
      deleteConversation(chatId, {
        onSuccess: () => {
          // Deleting the open conversation would leave a dangling view, so fall back to a fresh chat.
          if (selectedChatId === chatId) {
            onNewChat()
          }
        },
      })
    },
    [deleteConversation, selectedChatId, onNewChat],
  )

  const handleAgentItemClick = useCallback(
    (agentClickedId: string) => {
      // Every assistant shortcut starts a fresh session, including the active assistant.
      onClearConversation?.()
      onSelectAgent?.(agentClickedId)
      closeSidebarOnMobile()
    },
    [onClearConversation, onSelectAgent, closeSidebarOnMobile],
  )

  // Improved section mapping based on departments
  const agentSections = useMemo(() => {
    const sections = {
      favorites: [] as typeof agents,
      privileged: [] as typeof agents,
      departments: {} as Record<
        string,
        {
          agents: typeof agents
          icon_url?: string
        }
      >,
    }

    agents.forEach(agent => {
      // Add to favorites if preferred
      if (agent.is_preferred) {
        sections.favorites.push(agent)
      }

      // Add to privileged section
      if (agent.type === 'privileged') {
        sections.privileged.push(agent)
      } else {
        // Add to department sections for non-privileged agents
        const department = agent.department
        const departmentName = department?.name || 'Uncategorized'

        if (!sections.departments[departmentName]) {
          sections.departments[departmentName] = {
            agents: [],
            icon_url: department?.icon_url,
          }
        }
        sections.departments[departmentName].agents.push(agent)
      }
    })

    return sections
  }, [agents])

  const sortedChats = useMemo(
    () => [...chats].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
    [chats],
  )

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }, [])

  const handleLibraryClick = useCallback(() => {
    navigate('/library')
    closeSidebarOnMobile()
  }, [navigate, closeSidebarOnMobile])

  // Initialize intersection observer for infinite scroll using react-intersection-observer
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: '200px',
  })

  // Privileged agents are shown inline above the chat history when the
  // deployment enables the agents feature (runtime flag).
  const showAssistantsTab = useFeature(Feature.AGENTS)
  const analyticsEnabled = useFeature(Feature.ANALYTICS)

  // Trigger fetchNextPage when the sentinel element comes into view
  React.useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  const searchInput = (
    <InputGroup size='md' mb={0}>
      <InputLeftElement pointerEvents='none' w={9} h={10}>
        <Box color='chat.muted'>
          <ChatGlyph name='search' size={18} />
        </Box>
      </InputLeftElement>
      <Input
        placeholder={t('sidebar.searchConversations', { defaultValue: 'Search conversations...' } as {
          defaultValue: string
        })}
        id='sidebar-history-search'
        aria-label={t('sidebar.searchConversations')}
        autoFocus
        onKeyDown={event => {
          if (event.key === 'Escape') {
            setIsSearchOpen(false)
            setSearchQuery('')
            searchButtonRef.current?.focus()
          }
        }}
        value={searchQuery}
        onChange={handleSearchChange}
        fontSize='md'
        h={10}
        pl={10}
        color='chat.ink'
        borderRadius='chat.md'
        borderColor='chat.border'
        bg='chat.bubble'
        boxShadow='none'
        transition='border-color 0.2s, box-shadow 0.2s'
        _placeholder={{
          color: 'chat.muted',
        }}
        _focus={{
          borderColor: 'chat.focus',
          boxShadow: '0 0 0 1px var(--chakra-colors-chat-focus)',
        }}
      />
    </InputGroup>
  )

  return (
    <>
      <Flex
        {...props}
        as='aside'
        aria-hidden={!isOpen ? true : undefined}
        visibility={!isOpen ? 'hidden' : undefined}
        direction='column'
        h='100dvh'
        pt={isMobile ? 'env(safe-area-inset-top, 0px)' : 0}
        pb={isMobile ? 'env(safe-area-inset-bottom, 0px)' : 0}
        bg='chat.sidebar'
        color='chat.ink'
        // Rounded only as an overlay drawer (mobile); flush when docked beside the chat.
        borderTopRightRadius={isMobile ? 'md' : 0}
        borderBottomRightRadius={isMobile ? 'md' : 0}
        position='fixed'
        left={0}
        top={0}
        zIndex={1000}
        overflow='hidden'
        w={drawerWidth}
        maxW={drawerWidth}
        minW={drawerWidth}
        // -100% hides it fully regardless of width (85vw on mobile, 300px docked).
        transform={isOpen ? 'translateX(0)' : 'translateX(-100%)'}
        transition='transform 0.3s ease-in-out'
      >
        <Flex align='center' justify='space-between' px={4} py={3} minH={16} flexShrink={0}>
          {showWordmark ? <ChatWordmark size={26} /> : <Box />}
          <HStack spacing={1}>
            <SidebarToggleButton
              isOpen={isOpen}
              onToggle={isExternallyControlled ? onSidebarToggle : onCloseInternal}
            />
          </HStack>
        </Flex>

        <Flex direction='column' flex='1' minH={0} overflowY='hidden' mx={2}>
          <Box mb={showAssistantsTab ? 0 : 3}>
            <SidebarItem
              label={t('common.newChat')}
              icon={<ChatGlyph name='chat' />}
              highlightIcon
              showLabel={!isMinimized}
              onClick={() => {
                if (defaultAgent && onSelectAgent) {
                  handleAgentItemClick(defaultAgent.id)
                  return
                }
                onNewChat()
                closeSidebarOnMobile()
              }}
            />
          </Box>
          {/* Privileged agents */}
          {showAssistantsTab && (
            <>
              <VStack align='stretch' spacing={0}>
                {isLoadingAgents ? (
                  <Flex justify='center' align='center' py={4}>
                    <Spinner size='sm' color='primary' />
                    <Text ml={2} fontSize='sm' color='gray.500'>
                      {t('sidebar.loadingPrivilegedAgents', { defaultValue: 'Loading...' })}
                    </Text>
                  </Flex>
                ) : agentError ? (
                  <Text py={2} color='red.500'>
                    {agentError}
                  </Text>
                ) : agentSections.privileged.length > 0 ? (
                  [...agentSections.privileged]
                    .filter(agent => agent.id !== defaultAgent?.id)
                    .sort((a, b) => {
                      const order = { tipp: 0, gpt: 1, web: 2 }
                      return (
                        (order[a.slug as keyof typeof order] ?? 999) - (order[b.slug as keyof typeof order] ?? 999)
                      )
                    })
                    .map(agent => (
                      <SidebarItem
                        key={agent.id}
                        label={agent.name}
                        icon={getAgentIcon(agent.profile_picture_url, 20)}
                        highlightIcon
                        onClick={() => handleAgentItemClick(agent.id)}
                        showLabel={!isMinimized}
                      />
                    ))
                ) : (
                  <Text py={2}>{t('sidebar.noAssistantsAvailable')}</Text>
                )}
                {/* Favourited (non-privileged) agents appear directly under the
                    always-on privileged agent so users can pin library agents here. */}
                {agentSections.favorites
                  .filter(agent => agent.type !== 'privileged')
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(agent => (
                    <SidebarItem
                      key={agent.id}
                      label={agent.name}
                      icon={getAgentIcon(agent.profile_picture_url, 20, agent.type === 'normal')}
                      highlightIcon
                      onClick={() => handleAgentItemClick(agent.id)}
                      showLabel={!isMinimized}
                    />
                  ))}
                <SidebarItem
                  key='library'
                  highlightIcon
                  label={t('library.library', { defaultValue: 'Library' })}
                  icon={<ChatGlyph name='library' />}
                  isSelected={isLibraryPage}
                  onClick={handleLibraryClick}
                  showLabel={!isMinimized}
                />
              </VStack>
              <Box h={3} />
            </>
          )}

          {/* Chat history; only the list below its heading scrolls. */}
          <VStack align='stretch' spacing={1} flex='1' minH={0}>
            <Flex align='center' justify='space-between' w='100%' pl={2} pr={1} mb={1}>
              <Heading size='xs' fontFamily='body' color='chat.muted'>
                {t('sidebar.conversations')}
              </Heading>
              <IconButton
                ref={searchButtonRef}
                aria-label={t('sidebar.searchConversations')}
                aria-expanded={isSearchOpen}
                aria-controls='sidebar-history-search'
                icon={<ChatGlyph name='search' />}
                onClick={() => {
                  setIsSearchOpen(open => !open)
                  if (isSearchOpen) setSearchQuery('')
                }}
                variant='unstyled'
                display='flex'
                alignItems='center'
                justifyContent='center'
                minW={{ base: 12, md: 9 }}
                h={{ base: 12, md: 9 }}
                borderRadius='chat.sm'
                bg='transparent'
                color='chat.muted'
                _hover={{ opacity: 0.8 }}
                _focusVisible={{ outline: '2px solid', outlineColor: 'chat.focus', outlineOffset: '2px' }}
              />
            </Flex>
            {isSearchOpen && (
              <Box w='100%' pb={2}>
                {searchInput}
              </Box>
            )}

            <Box flex='1' minH={0} overflowY='auto'>
              {isLoading ? (
                <Flex justify='center' align='center' height='100px'>
                  <Spinner size='md' color='primary' />
                </Flex>
              ) : !error && chats.length === 0 && searchQuery.trim().length === 0 ? (
                <Text color='secondary' pl={2} mb={4}>
                  {t('sidebar.noConversationsAvailable', { defaultValue: 'No conversations available' } as {
                    defaultValue: string
                  })}
                </Text>
              ) : !error && chats.length === 0 && searchQuery.trim().length > 0 ? (
                <Text color='secondaryAlpha' pl={2}>
                  {t('sidebar.noSearchResults', { defaultValue: 'No conversations matching your search' } as {
                    defaultValue: string
                  })}
                </Text>
              ) : (
                <React.Fragment>
                  {sortedChats.length > 0 && (
                    <VStack mb={6} spacing={0} align='stretch'>
                      {sortedChats.map((chat: ChatSummarySchema) => {
                        const chatAgent = agents.find(agent => agent.id === chat.agentId)
                        return (
                          <ConversationRow
                            key={chat.id}
                            chat={chat}
                            icon={getAgentIcon(
                              chat.agentIcon || chatAgent?.profile_picture_url,
                              18,
                              chatAgent?.type === 'normal',
                            )}
                            onClick={() => handleChatItemClick(chat.id)}
                            isSelected={selectedChatId === chat.id}
                            showLabel={!isMinimized}
                            onRename={newTitle => renameConversation({ chatId: chat.id, title: newTitle })}
                            onDelete={() => handleDeleteChat(chat.id)}
                          />
                        )
                      })}
                    </VStack>
                  )}

                  {/* Invisible sentinel element for infinite scroll */}
                  {hasNextPage && <Box ref={inViewRef} height='1px' />}

                  {/* Loading indicator for infinite scroll */}
                  {isFetchingNextPage && (
                    <Flex justify='center' align='center' py={4}>
                      <Spinner size='sm' color='primary' />
                      <Text ml={2} fontSize='sm' color='gray.500'>
                        {t('sidebar.loadingMoreConversations', { defaultValue: 'Loading more conversations...' })}
                      </Text>
                    </Flex>
                  )}
                </React.Fragment>
              )}
            </Box>
          </VStack>
        </Flex>
        <Divider borderColor={dividerColor} />
        <Box mt='auto' mx={2} py={2} flexShrink={0}>
          <VStack spacing={0} align='stretch'>
            {/* Reshaped admins find Analytics and Feature flags on the Settings page, so a demo shows the end-user sidebar. */}
            {currentUser?.is_superuser && !currentUser.is_reshaped_admin && analyticsEnabled && (
              <SidebarItem
                icon={<BarChart3 size={16} />}
                label='Analytics'
                onClick={() => {
                  navigate('/analytics')
                  closeSidebarOnMobile()
                }}
                showLabel={!isMinimized}
              />
            )}
            <SidebarItem
              icon={<ChatGlyph name='help' />}
              label={t('sidebar.userGuide')}
              onClick={() => {
                openInformationModal()
                closeSidebarOnMobile()
              }}
              showLabel={!isMinimized}
            />
            <SidebarItem
              icon={
                <Avatar
                  size='xs'
                  name={sanitizeNameForInitials(user?.displayName || t('chat.anonymousUser') || 'Anonymous User')}
                  bg='chat.bubble'
                  color='chat.ink'
                />
              }
              label={user?.displayName || t('chat.anonymousUser') || 'Anonymous User'}
              onClick={() => navigate('/settings')}
              showLabel={!isMinimized}
            />
          </VStack>
          {!isMinimized && <SidebarSignature />}
        </Box>
      </Flex>
      <GeneralInformationModal isOpen={isInformationModalOpen} onClose={closeInformationModal} />
    </>
  )
}

export default React.memo(ChatSidebar)
